import { initMonitoringSdk, MonitoringSdk, trace } from '@dvag/dfs-monitoring-sdk-fe';
import type { Attributes } from '@dvag/dfs-monitoring-sdk-fe';
import { getEnvironmentApiUrls } from '@dvag/dfs-constant-config/api-urls';
import { getContext } from '@dvag/dfs-orchestrator-client';

export type MonitoringProvider = Pick<MonitoringSdk, 'forceFlush'>;

export type Monitoring = {
  initialized: boolean;
  provider: MonitoringProvider | null;
  init: () => void;
  flush: () => Promise<void>;
};

const monitoring: Monitoring = {
  initialized: false,
  provider: null,
  init: () => {
    const advisorId = getContext().user.advisor.id;

    const apiUrls = getEnvironmentApiUrls();

    const requestTracingConfig = {
      // Must mirror what the call sites resolve to, or `traceparent` stops propagating.
      propagateTraceHeaderCorsUrls: [
        apiUrls.advisor,
        apiUrls.person,
        apiUrls.appointment,
        apiUrls.consultationService,
        apiUrls.recap,
        apiUrls.registration,
        apiUrls.kiVp,
        apiUrls.graphQl.advisor,
        apiUrls.graphQl.person,
      ].map((it) => new RegExp(new URL(it).hostname)),
      ignoreUrls: [
        new RegExp(new URL(apiUrls.otelCollector.endpoint).hostname),
        /applicationinsights\.azure\.com/,
      ],
    };

    const initMonitoringAttributes = {
      serviceName: 'dfs-vp-digital.dfs-advisor-fe.APP-00122',
      collectorEndpoint: getEnvironmentApiUrls().otelCollector.endpoint,
      instrumentationKey: getEnvironmentApiUrls().otelCollector.instrumentationKey,
      userId: advisorId,
      autoInstrumentations: {
        '@opentelemetry/instrumentation-fetch': requestTracingConfig,
        '@opentelemetry/instrumentation-xml-http-request': requestTracingConfig,
      },
    };

    monitoring.provider = initMonitoringSdk(initMonitoringAttributes);
    monitoring.initialized = true;
  },
  flush: async () => {
    await monitoring.provider?.forceFlush();
  },
};

export const getMonitoring = () => {
  const initTelemetry = () => {
    if (monitoring.initialized) {
      return;
    }

    monitoring.init();
  };

  const tracer = trace.getTracer('dfs-advisor-fe');

  const trackEvent = (event: { name: string; properties: Attributes }) => {
    const activeSpan = trace.getActiveSpan();

    if (activeSpan) {
      activeSpan.addEvent(event.name, event.properties);
      return;
    }

    const span = tracer.startSpan(event.name);

    try {
      span.addEvent(event.name, event.properties);
    } finally {
      span.end();
    }
  };

  const trackException = (
    error: unknown,
    spanName: string | undefined = 'application.exception',
  ) => {
    if (!(error instanceof Error)) {
      return;
    }

    const activeSpan = trace.getActiveSpan();

    if (activeSpan) {
      activeSpan.recordException(error);
      return;
    }

    const span = tracer.startSpan(spanName);

    try {
      span.recordException(error);
    } finally {
      span.end();

      if (monitoring.initialized) {
        monitoring.flush();
      }
    }
  };

  return { initTelemetry, trackEvent, trackException, flush: monitoring.flush };
};
